import moment from 'moment';
import { RELATIVE_ERROR_MESSAGES } from './constants';
import { RelativeValidationChecks, ValidDateRanges } from './types';

/** trims all white spaces out of a string. */
export const trimWhiteSpace = (string: string) => string.replace(/\s/g, '');

/**
 * Validates that a date string includes only the correct tokens.
 * For more info on the digits allowed: https://support.iterable.com/hc/en-us/articles/360012688571-Relative-Date
 */
export const isValidRelativeDateString = (relativeDateString: string) => {
  const relStringRegex = /^\d+[yMwdHhms](\/[yMwdHhms])?$/;

  const trimmedDateString = trimWhiteSpace(relativeDateString);
  return trimmedDateString.split(/[+-]/).reduce((isValid, token, index) => {
    // if there is more than one empty '' in the split array, then what we're
    // looking at is a string like "+" or "90d+" or "3d-+", any of which are not gucci.

    if (token === '' && index === 0) {
      return true;
    }

    if (!isValid) {
      return false;
    }

    switch (token) {
      case '/d':
      case '/h':
      case '/H':
      case '/m':
      case '/M':
      case '/Y':
      case '/w':
      case '/s':
      case '/ms':
        return true;
      default:
        return !!token.match(relStringRegex);
    }
  }, true);
};

/**
 * Validates that a relative time string falls between a given start date and end date that are provided in the @validDateRange prop
 */
export const validateRelativeTime = (
  relativeDateString: string,
  validDateRange: ValidDateRanges,
): RelativeValidationChecks => {
  const now = moment();
  const trimmedDateString = trimWhiteSpace(relativeDateString);
  const transformations = trimmedDateString.match(/[-+]+(\d*)+[yMwdHhms]/g) || [];

  // relative dates may inlcude handlebars.
  // if so, we just assume they are corect.
  if (trimmedDateString.includes('{{')) {
    return {}; // empty object means all is good;
  }

  if (!isValidRelativeDateString(trimmedDateString)) {
    return {
      isInvalid: true,
    };
  }

  transformations.forEach((amountOfTime) => {
    const match = amountOfTime.match(/[yMwdHhms]/);
    const change = amountOfTime.slice(0, 1);
    const type = match?.[0];
    const amount = parseInt(amountOfTime.slice(1, match?.index), 10);

    if (change === '+') {
      // @ts-expect-error // https://stackoverflow.com/questions/41768864/moment-add-only-works-with-literal-values
      now.add(amount, type);
    } else if (change === '-') {
      // @ts-expect-error // https://stackoverflow.com/questions/41768864/moment-add-only-works-with-literal-values
      now.subtract(amount, type);
    }
  });

  return {
    isBefore: now.isBefore(validDateRange.start),
    isAfter: now.isAfter(validDateRange.end),
  };
};

export const getErrorMessage = (validationChecks: RelativeValidationChecks) => {
  switch (true) {
    case validationChecks.isBefore:
      return RELATIVE_ERROR_MESSAGES.pastDate;
    case validationChecks.isAfter:
      return RELATIVE_ERROR_MESSAGES.futureDate;
    case validationChecks.isInvalid:
      return RELATIVE_ERROR_MESSAGES.invalidDate;
    default:
      return '';
  }
};
